import { Fieldset } from '@calendly/form/parts'
import { RadioField } from '@calendly/ui/form/radio-field'
import { useState } from 'react'

import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell'
import { publisherWorkflowsT } from 'services/workflows/publisher_workflows_i18n'

import {
  DEFAULT_TIME,
  DEFAULT_UNITS,
  BOOKING_WHEN_INVITEE_BOOKED,
  BOOKING_WHEN_INVITEE_NO_SHOW,
  BOOKING_BEFORE_MEETING_STARTED,
  BOOKING_WHEN_MEETING_SCHEDULED,
  BOOKING_WHEN_MEETING_CANCELED,
  BOOKING_WHEN_MEETING_FINISHED,
  BOOKING_WHEN_MEETING_STARTED,
  BOOKING_WHEN_MEETING_RESCHEDULED
} from './consts'
import {
  TimingOptionsBlock,
  WhenBlock,
  WhenRow
} from './styles'
import { TimingInput } from './timing_input'
import { howLongText } from '../../../i18n'
import { SubTitle } from '../../../styles'

const BeforeTiming = ({ isAppShellRebrand, ...props }) => (
  <TimingInput notZero isAppShellRebrand={isAppShellRebrand} {...props} />
)

const WhenTiming = ({
  value,
  units,
  immediatelyLabel,
  onChange,
  onError,
  isAppShellRebrand
}) => {
  const immediate = (value === 0 || value === null)

  const [localValue, setValue] = useState(immediate ? DEFAULT_TIME : value)
  const [localUnits, setUnits] = useState(immediate ? DEFAULT_UNITS : units)

  const onTimingChange = (newValue, newUnits) => {
    setValue(newValue)
    setUnits(newUnits)
    onChange(newValue, newUnits)
  }

  const unitsLabel = publisherWorkflowsT(`publisher.workflows.forms.trigger.units.${localUnits}`, isAppShellRebrand)

  return (
    <WhenBlock>
      <WhenRow>
        <RadioField
          checked={immediate}
          onChange={() => { onChange(0) }}
          label={immediatelyLabel}
        />
      </WhenRow>
      <WhenRow>
        <RadioField
          checked={!immediate}
          onChange={() => { onChange(localValue, localUnits) }}
          aria-label={`${localValue} ${unitsLabel}`}
        />
        <TimingInput
          value={localValue}
          units={localUnits}
          onChange={onTimingChange}
          onError={onError}
          isAppShellRebrand={isAppShellRebrand}
        />
      </WhenRow>
    </WhenBlock>
  )
}

const BookingWhenInviteeBooked = ({ isAppShellRebrand, ...props }) => (
  <WhenTiming
    immediatelyLabel={publisherWorkflowsT('publisher.workflows.triggers.booking.when.invitee_booked.immediately', isAppShellRebrand)}
    isAppShellRebrand={isAppShellRebrand}
    {...props}
  />
)

const BookingWhenMeetingScheduled = ({ isAppShellRebrand, ...props }) => (
  <WhenTiming
    immediatelyLabel={publisherWorkflowsT('publisher.workflows.triggers.booking.when.invitee_booked.immediately', isAppShellRebrand)}
    isAppShellRebrand={isAppShellRebrand}
    {...props}
  />
)

const BookingWhenMeetingStarted = ({ isAppShellRebrand, ...props }) => (
  <WhenTiming
    immediatelyLabel={publisherWorkflowsT('publisher.workflows.triggers.booking.when.meeting_started.immediately', isAppShellRebrand)}
    isAppShellRebrand={isAppShellRebrand}
    {...props}
  />
)

const BookingWhenMeetingFinished = ({ isAppShellRebrand, ...props }) => (
  <WhenTiming
    immediatelyLabel={publisherWorkflowsT('publisher.workflows.triggers.booking.when.meeting_finished.immediately', isAppShellRebrand)}
    isAppShellRebrand={isAppShellRebrand}
    {...props}
  />
)

const BookingWhenMeetingCanceled = ({ isAppShellRebrand, ...props }) => (
  <WhenTiming
    immediatelyLabel={publisherWorkflowsT('publisher.workflows.triggers.booking.when.meeting_canceled.immediately', isAppShellRebrand)}
    isAppShellRebrand={isAppShellRebrand}
    {...props}
  />
)

const BookingWhenMeetingRescheduled = ({ isAppShellRebrand, ...props }) => (
  <WhenTiming
    immediatelyLabel={publisherWorkflowsT('publisher.workflows.triggers.booking.when.meeting_rescheduled.immediately', isAppShellRebrand)}
    isAppShellRebrand={isAppShellRebrand}
    {...props}
  />
)

const BookingWhenInviteeNoShow = ({ isAppShellRebrand, ...props }) => (
  <WhenTiming
    immediatelyLabel={publisherWorkflowsT('publisher.workflows.triggers.booking.when.invitee_no_show.immediately', isAppShellRebrand)}
    isAppShellRebrand={isAppShellRebrand}
    {...props}
  />
)

const timingTypes = {
  [BOOKING_WHEN_INVITEE_BOOKED]: BookingWhenInviteeBooked,
  [BOOKING_WHEN_INVITEE_NO_SHOW]: BookingWhenInviteeNoShow,
  [BOOKING_BEFORE_MEETING_STARTED]: BeforeTiming,
  [BOOKING_WHEN_MEETING_SCHEDULED]: BookingWhenMeetingScheduled,
  [BOOKING_WHEN_MEETING_STARTED]: BookingWhenMeetingStarted,
  [BOOKING_WHEN_MEETING_FINISHED]: BookingWhenMeetingFinished,
  [BOOKING_WHEN_MEETING_CANCELED]: BookingWhenMeetingCanceled,
  [BOOKING_WHEN_MEETING_RESCHEDULED]: BookingWhenMeetingRescheduled
}

const TimingOptions = ({ triggerType, isAppShellRebrand, ...props }) => {
  if (!triggerType) { return null }

  const Component = timingTypes[triggerType]

  return <Component {...props} isAppShellRebrand={isAppShellRebrand} />
}

export const Timing = (props) => {
  const { triggerType } = props
  const isAppShellRebrand = useProductRefreshAppShell()

  if (!triggerType) {
    return null
  }

  return (
    <Fieldset>
      <SubTitle as="legend">{howLongText(triggerType, isAppShellRebrand)}</SubTitle>
      <TimingOptionsBlock>
        <TimingOptions {...props} isAppShellRebrand={isAppShellRebrand} />
      </TimingOptionsBlock>
    </Fieldset>
  )
}
